import {
  CellContext,
  createColumnHelper,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table'

import dayjs from 'dayjs'
import { useMemo } from 'react'

import {
  formatInteger,
  formatTwoDecimal,
  formatUsd,
} from '../../../utils/formatting'
import { getCompanyV2Url } from '../../../utils/getCompanyUrl'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { TruncatedTextCell } from '../CommonTableCells/TruncatedTextCell'
import {
  AccountOwnerPill,
  CampaignsCell,
  ContactTagCellV2,
  DistributorsCell,
  GPOsCell,
  StagePill,
  TaglistCell,
} from '../../FbUI/StagePill'
import ExpandedDealsRow from './ExpandedDealsRow'
import apiService from 'services/api'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { featureFlagService } from 'utils/featureFlagService'
import { DealReport } from 'models/deals_reports'

interface UseDealsTableColumnsProps {
  sorting: SortingState
  showCompanyColumn?: boolean
  enabledReportDeals?: boolean
}

export function useDealsTableColumnsV2({
  sorting,
  showCompanyColumn,
}: UseDealsTableColumnsProps): ColumnDef<DealReport>[] {
  const api = apiService()
  const { preferences } = usePreferences()
  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const dealReportColumnHelper = createColumnHelper<DealReport>()
  const reportsColumnHelper = createTableColumnHelper({
    columns: DEAL_COLUMNS,
    tooltips,
  })
  const { enableEmailIntegration } = featureFlagService()
  const dealsReportsColumns = useMemo(() => {
    let currentColumns: ColumnDef<DealReport, any>[] = [
      {
        id: 'expanded-row',
        header: '',
        cell: (info: CellContext<DealReport, unknown>) => {
          const row = info.row.original
          return (
            <ExpandedDealsRow dealId={row.id} productName={row.product_name} />
          )
        },
      },
      ...(showCompanyColumn
        ? [
            dealReportColumnHelper.accessor('cc_id', {
              ...reportsColumnHelper.retrieveColumn('company', {
                meta: {
                  isEditable: false,
                  isFirstColumn: true,
                },
              }),
              header: () => null,
              cell: (info) => {
                const row = info.row.original
                const companyName = row.cc_company_name

                return (
                  <TruncatedLinkCell
                    text={companyName || ''}
                    to={`${getCompanyV2Url({
                      company_type: row.cc_company_type,
                      chain_id: row.cc_chain_id,
                      university_id: row.cc_university_id,
                      k12district_id: row.cc_k12district_id,
                      lodging_id: row.cc_lodging_id,
                      id: row.cc_id,
                    })}`}
                  />
                )
              },
              size: 350,
            }),
          ]
        : []),
      dealReportColumnHelper.accessor('product_id', {
        ...reportsColumnHelper.retrieveColumn('product', {
          meta: {
            isEditable: showCompanyColumn,
            isFirstColumn: !showCompanyColumn,
          },
        }),
        header: () => (showCompanyColumn ? 'Product' : ''),
        size: 250,
        cell: (info) => {
          const row = info.row.original
          const productName = row.product_name
          return <TruncatedTextCell text={productName || ''} />
        },
      }),
      dealReportColumnHelper.accessor('deal_display_name', {
        ...reportsColumnHelper.retrieveColumn('deal_name'),
        header: () => 'Deal Name',
        size: 250,
        cell: (info) => {
          const row = info.row.original
          const dealName = row.deal_display_name || row.cc_company_name || '-'
          return <TruncatedTextCell text={dealName} />
        },
      }),
      dealReportColumnHelper.accessor('product_variant_name', {
        ...reportsColumnHelper.retrieveColumn('product_variant'),
        header: () => 'Product Variant',
        size: 200,
        cell: (info) => {
          const row = info.row.original
          return row.product_variant_name || '-'
        },
      }),
      dealReportColumnHelper.accessor('cc_doors_cnt', {
        ...reportsColumnHelper.retrieveColumn('company_size'),
        size: 100,
        cell: (info) => {
          const value = info.row.original.cc_doors_cnt
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('cc_category_id', {
        ...reportsColumnHelper.retrieveColumn('category'),
        cell: (info) => {
          return (
            categories?.find(
              (a) => a.id === (info.getValue() as unknown as number)
            )?.name ?? '-'
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_sub_category_id', {
        ...reportsColumnHelper.retrieveColumn('sub_category'),
        cell: (info) => {
          let subCategoryName = '-'
          for (const category of categories ?? []) {
            const subCategory = category.sub_categories.find(
              (subCat) => subCat.id === (info.getValue() as unknown as number)
            )
            if (subCategory) {
              subCategoryName = subCategory.name
              break
            }
          }
          return subCategoryName
        },
      }),
      dealReportColumnHelper.accessor('sales_stage_id', {
        ...reportsColumnHelper.retrieveColumn('sales_stage'),
        size: 250,
        cell: (info) => {
          if (!info.row?.original?.sales_stage_name) return <>-</>
          return <StagePill stageId={info.row.original?.sales_stage_id} />
        },
      }),
      dealReportColumnHelper.accessor('last_datetime_stage_changed', {
        ...reportsColumnHelper.retrieveColumn('last_datetime_stage_changed'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().diff(dayjs(value), 'day')} days`
        },
      }),
      dealReportColumnHelper.accessor('expected_days_to_close', {
        ...reportsColumnHelper.retrieveColumn('expected_days_to_close'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().add(value, 'day').format('MMM DD, YYYY')}`
        },
      }),
      dealReportColumnHelper.accessor('close_date_override', {
        ...reportsColumnHelper.retrieveColumn('close_date_override'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return dayjs(value).format('MMM DD, YYYY')
        },
      }),
      dealReportColumnHelper.accessor('account_owner_id', {
        ...reportsColumnHelper.retrieveColumn('account_owner'),
        size: 250,
        cell: (info) => {
          return <AccountOwnerPill account_owner_id={info.getValue()} />
        },
      }),
      dealReportColumnHelper.accessor('days_since_reply', {
        ...reportsColumnHelper.retrieveColumn('days_since_reply'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      dealReportColumnHelper.accessor('days_since_sent', {
        ...reportsColumnHelper.retrieveColumn('days_since_sent'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      dealReportColumnHelper.accessor('origin_campaign_id', {
        ...reportsColumnHelper.retrieveColumn('origin_campaign'),
        size: 260,
        cell: (info) => {
          const campIds = info.row.original.origin_campaign_id
            ? [info.row.original.origin_campaign_id]
            : []
          return <CampaignsCell campaignsIds={campIds} />
        },
      }),
      dealReportColumnHelper.accessor('annual_revenue', {
        ...reportsColumnHelper.retrieveColumn('annual_revenue'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      dealReportColumnHelper.accessor('monthly_revenue_predicted', {
        ...reportsColumnHelper.retrieveColumn('monthly_revenue_predicted'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      dealReportColumnHelper.accessor('monthly_revenue_override', {
        ...reportsColumnHelper.retrieveColumn('monthly_revenue_override'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      dealReportColumnHelper.accessor('annual_volume', {
        ...reportsColumnHelper.retrieveColumn('annual_volume'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('monthly_volume_predicted', {
        ...reportsColumnHelper.retrieveColumn('monthly_volume_predicted'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('annual_brand_opp', {
        ...reportsColumnHelper.retrieveColumn('annual_brand_opp'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('annual_total', {
        ...reportsColumnHelper.retrieveColumn('annual_total'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('monthly_volume_override', {
        ...reportsColumnHelper.retrieveColumn('monthly_volume_override'),
        size: 175,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('cc_taglist', {
        ...reportsColumnHelper.retrieveColumn('taglist'),
        size: 260,
        cell: (info) => {
          return (
            <TaglistCell
              taglistIds={info.row.original?.cc_taglist?.map((tag) => tag.id!)}
              tagType="products"
              asc={
                sorting?.length && sorting[0].id === 'taglist'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('contact_tags', {
        ...reportsColumnHelper.retrieveColumn('contact_tags'),
        size: 260,
        cell: (info) => {
          return (
            <ContactTagCellV2
              contact_tags={info.row.original.contact_tags?.map(
                (tag) => tag.id!
              )}
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_distributors', {
        ...reportsColumnHelper.retrieveColumn('distributors'),
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell
              distributorsIds={info.row.original?.cc_distributors?.map(
                (dist) => dist.id!
              )}
              asc={
                sorting?.length && sorting[0].id === 'distributors'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_gpos', {
        ...reportsColumnHelper.retrieveColumn('gpos'),
        size: 260,
        cell: (info) => {
          return (
            <GPOsCell
              gposIds={info.row.original?.cc_gpos?.map((gpo) => gpo.id!)}
              asc={
                sorting?.length && sorting[0].id === 'gpos'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_note_count', {
        ...reportsColumnHelper.retrieveColumn('note_count'),
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('cc_contact_count', {
        ...reportsColumnHelper.retrieveColumn('contact_count'),
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('days_since_last_modified', {
        ...reportsColumnHelper.retrieveColumn('days_since_last_modified'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      dealReportColumnHelper.accessor('days_since_deal_last_modified', {
        ...reportsColumnHelper.retrieveColumn('days_since_deal_last_modified'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      dealReportColumnHelper.accessor('estimated_meals_per_day', {
        ...reportsColumnHelper.retrieveColumn('estimated_meals_per_day'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('operational_schools', {
        ...reportsColumnHelper.retrieveColumn('operational_schools'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('modified', {
        ...reportsColumnHelper.retrieveColumn('modified'),
        size: 150,
        cell: (info) => {
          return dayjs(info.getValue()).format('MM/DD/YYYY')
        },
      }),
      dealReportColumnHelper.accessor('created', {
        ...reportsColumnHelper.retrieveColumn('created'),
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      }),
      dealReportColumnHelper.accessor('doors_count', {
        ...reportsColumnHelper.retrieveColumn('doors_count'),
        size: 150,
        cell: (info) => info.getValue() || '-',
      }),
    ]

    if (!enableEmailIntegration) {
      currentColumns = currentColumns.filter(
        (column) =>
          column.id !== 'days_since_reply' && column.id !== 'days_since_sent'
      )
    }

    return currentColumns
  }, [categories, tooltips])

  return dealsReportsColumns
}
